import { defineStore } from 'pinia'
import shp from 'shpjs'
import { FeatureCollection } from 'geojson'
import csv from 'csvtojson'
import typedStore from '@/store/typedStore'
import _countBy from 'lodash/countBy'
import _range from 'lodash/range'

import {
  postNodes,
  postConfigItem,
  getSnowflakeData,
} from '@/api'
import {
  FieldMapItem,
  FieldTypes,
  LevelField,
  Path,
  TreeNode,
} from '@/types'
import {
  TAB_COLORS,
  DEFAULT_STORY_KEY,
} from '@/constants'
import { LevelsHelper } from '@/store/levelsHelper'

export interface IUploadState {
  csvLoaded: Boolean,
  shpLoaded: Boolean,
  rows: { [column: string]: string }[],
  hasRows: Boolean,
  fieldMap: FieldMapItem[],
  levelCount: number,
  geom: FeatureCollection
}

function paths (maxLevel: number) {
  const paths = [Path.root, Path.subroot]

  const children = typedStore.visualisationData.childrenByPath(Path.root)
  getListOfPaths(paths, maxLevel, children)
  return paths
}

function getListOfPaths (result: Path[], maxLevel: number, children?: TreeNode[]) {
  if (children) {
    children.forEach((child) => {
      result.push(child.path)
      if (child.level < maxLevel) getListOfPaths(result, maxLevel, child.children)
    })
  }
}

export const useUploadStore = defineStore({
  id: 'upload',
  state: (): IUploadState => ({
    csvLoaded: false,
    shpLoaded: false,
    rows: [{}],
    hasRows: false,
    fieldMap: [],
    levelCount: typedStore.features.useNLevels ? 1 : 3,
    geom: {
      type: 'FeatureCollection',
      features: [],
    },
  }),
  getters: {

    headers (state: IUploadState):string[] { return Object.keys(state.rows[0]) },
    levelsHelper: (state: IUploadState):LevelsHelper => LevelsHelper.fromFieldMapItems(state.fieldMap),
    csvJson: (state: IUploadState):any => ({ rows: state.rows }),
    allLevels: (state: IUploadState):number[] => _range(1, state.levelCount + 1),
    levelFields (state: IUploadState): { [key:number]: LevelField } {
      return state.fieldMap.filter(f => f.level).reduce((a, v) => ({
        ...a,
        [v.level!]: {
          ...v,
          level: v.level!,
        },
      }), {})
    },

    level1Descendants (state: IUploadState):(level1Value: string, level: number) => string[] {
      return (level1Value: string, level: number) => {
        const level1Col = this.levelsHelper.columnForLevel(1)
        const column = this.levelsHelper.columnForLevel(level)
        return [...new Set(state.rows
          .filter(row => row[level1Col] === level1Value)
          .map(row => row[column])),
        ]
      }
    },

    noBlanks (state: IUploadState):(level: number) => boolean {
      return (level: number) => {
        const column = this.levelsHelper.columnForLevel(level)
        if (column && state.hasRows) {
          const hasBlanks = state.rows.find((row) => {
            return row[column].length === 0
          })
          return Boolean(!hasBlanks)
        } else {
          return true
        }
      }
    },

    validLevel (state: IUploadState):(level: number) => boolean {
      return (level: number) => {
        const column = this.levelsHelper.columnForLevel(level)
        if (column && state.hasRows) {
          const hasSlash = state.rows.find((row) => {
            return row[column].indexOf('/') > 0
          })
          return Boolean(!hasSlash)
        } else {
          return true
        }
      }
    },

    levelValues (state: IUploadState):(level: number) => string[] {
      return (level: number): string[] => {
        const key = this.levelsHelper.columnForLevel(level)
        return [...new Set(state.rows.map(r => r[key]))]
      }
    },

    uniqueLeafLevel (state: IUploadState):boolean {
      const column = this.levelsHelper.columnForLevel(state.levelCount)
      if (column && state.hasRows) {
        const unique = new Set(state.rows.map(r => r[column]))
        return unique.size === state.rows.length
      } else {
        return true
      }
    },
  },

  actions: {
    incrementLevelCount () {
      this.levelCount += 1
    },

    async getSnowflakeData ({ username, password, account, warehouse, db, schema, tableview }: { username: string, password: string, account: string, warehouse: string, db: string, schema: string, tableview: string }) {
      return getSnowflakeData({
        user: username,
        password: password,
        account: account,
        warehouse: warehouse,
        database: db,
        schema: schema,
        tableview: tableview,
      })
    },

    resetAllUploadData () {
      this.$reset()
    },

    async processCsv (payload: Blob) {
      return new Promise<void>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => {
          const rawCSVContent = csv({
            noheader: false,
          // output: "csv"
          })
          if (e?.target?.result) {
            rawCSVContent.fromString(e.target.result as string)
              .then((arrayJSONObj) => {
                try {
                  this.processArrayJSONObj(arrayJSONObj)
                } catch (e) {
                  reject(e)
                }
                resolve()
              })
          }
        }
        reader.readAsText(payload)
      })
    },

    processArrayJSONObj (payload: { [column: string]: string }[]) {
      this.setRows(payload)
      this.validateMapping()

      const oldFieldMap = this.fieldMap
      const fieldMap: FieldMapItem[] = this.headers.map((column) => {
        const oldField = oldFieldMap.find(f => f.column === column)
        if (oldField) {
          return oldField
        } else {
          const sampleValue = this.rows[0][column]
          return {
            column: column,
            type: !isNaN(Number(sampleValue)) ? FieldTypes.NUMERIC : FieldTypes.STRING,
            unit: '',
            total: false,
            level: 0,
            showBubbles: false,
            name: column
              .toLowerCase()
              .split(' ')
              .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
              .join(' '),
          }
        }
      })
      this.setFieldMap(fieldMap)
    },

    processShp (payload: Blob) {
      return new Promise<void>((resolve, reject) => {
        // Payload: file
        this.setShpLoaded(false)
        this.setGeom({
          type: 'FeatureCollection',
          features: [],
        })

        const reader = new FileReader()
        reader.onload = e => {
          if (e?.target?.result) {
            shp(e.target.result)
              .then((geojson) => {
                if ((Array.isArray(geojson))) reject(new Error("You shouldn't be uploading multiple shapefiles"))
                else this.setGeom({ type: 'FeatureCollection', features: geojson.features })
                resolve()
              })
              .catch((error) => {
                reject(error)
              })
          }
        }
        reader.readAsArrayBuffer(payload)
      })
    },

    async createNodes () {
      const allPaths = paths(this.levelCount).map(p => p.toString())
      const defaultStory = typedStore.visualisationData.allLevels.map((i) => `${DEFAULT_STORY_KEY}${i}`)
      return postNodes(allPaths.concat(defaultStory))
    },

    async addConfigToNodes () {
      // Non-leaf node paths only
      let count = 0
      return Promise.all(paths(this.levelCount - 1).map((path) => {
        const name = path.name
        const isStyled = path.level <= 1
        const config = {
          name,
          bubblePosition: [0, 0],
          headerPosition: [0, -100],
          colour: isStyled ? TAB_COLORS[count % TAB_COLORS.length] : null,
          order: isStyled ? count : null,
          icon: isStyled ? '' : null,
        }
        if (isStyled) count += 1
        return postConfigItem(path.toString(), 'config', config)
      }))
    },

    validateMapping () {
      this.setFieldMap(Array.from(this.fieldMap.filter(i => this.headers.includes(i.column))))
    },

    setCsvLoaded (payload: boolean) {
      this.csvLoaded = payload
    },

    setShpLoaded (payload: boolean) {
      this.shpLoaded = payload
    },

    setRows (payload: { [column: string]: string }[]) {
      this.rows = payload
      this.hasRows = true
    },

    setFieldMap (payload: FieldMapItem[]) {
      this.fieldMap = payload
      this.levelCount = Math.max(typedStore.features.useNLevels ? 1 : 3, ...payload.map(f => f.level || 0))
    },

    setGeom (payload: FeatureCollection) {
      this.geom = payload
    },

    updateFieldProperty (payload: { column: string, partialValue: any }) {
      const { column, partialValue } = payload
      const col = this.fieldMap.find(f => f.column === column) as any
      if (col) {
        for (const key in partialValue) {
          col[key] = partialValue[key]
        }
      }
    },

    deleteLevel (level: number) {
      this.levelCount -= 1
      for (const levelField of this.levelsHelper.levels) {
        if (levelField.level === level) {
          this.updateLevel({ column: levelField.column, level: 0 })
        }
        if (levelField.level > level) {
          this.updateLevel({ column: levelField.column, level: levelField.level - 1 })
        }
      }
    },

    reorderLevels (newLevels: number[]) {
      for (const f of this.fieldMap.filter(f => f.level)) {
        f.level = newLevels[f.level! - 1]
      }
    },

    updateLevel (payload: { level: number, column: string }) {
      const { level, column } = payload
      const prev = this.fieldMap.find(f => f.level === level)
      const col = this.fieldMap.find(f => f.column === column)
      if (col && col === prev) {
        // Re-ordered case, levels are correct but showBubbles may be invalid
        const showBubblesAbove = level === 1 || (this.levelsHelper.fieldForLevel(level - 1)?.showBubbles || false)
        const showBubblesBelow = level === this.levelCount || (this.levelsHelper.fieldForLevel(level + 1)?.showBubbles || false)
        col.showBubbles = col.showBubbles || !(showBubblesAbove && showBubblesBelow)
      } else {
        // User dropdown selection case
        if (prev) {
          prev.level = 0
        }
        if (col) {
          col.level = level
          col.showBubbles = typedStore.features.useNLevels ? true : level === 3
        }
      }
    },
  },
})
